import { ChangeEvent, FunctionComponent } from 'react'
import { FieldProps } from '@rjsf/utils'
import isUndefined from 'lodash/isUndefined'

import { useAppSelector } from '../../../../../../../hooks'
import { selectActiveGeometryGroup } from '../../../../../../../imup/slices/geometry'
import { Project } from '../../../../../../../models/project'
import { DRAWABLE_TYPES } from '../../../../../../../shared/constants/drawable-types'
import { renderErrors } from '../../../../../../../utils/formSchema/formSchema-helper-functions'

import './dropdown.scss'

export const DropdownField: FunctionComponent<FieldProps> = (props: FieldProps) => {
    const project: Project = useAppSelector((state) => state.drawable.project)
    const activeDrawableGroup = useAppSelector(selectActiveGeometryGroup)

    const { name, formData = '', schema, uiSchema, idSchema, onChange, errorSchema, required, readonly } = props

    const hasPlaceholder = props?.uiSchema?.['ui:options']?.dropdownWithPlaceholder
    const symbol = props?.uiSchema?.['ui:options']?.symbol

    const is2DDirectionFieldInFloorOrRoofSystem =
        name === 'direction' &&
        !project.is3D &&
        (activeDrawableGroup?.type === DRAWABLE_TYPES.FLOOR_SYSTEM ||
            activeDrawableGroup?.type === DRAWABLE_TYPES.FLOOR_SYSTEMS ||
            activeDrawableGroup?.type === DRAWABLE_TYPES.ROOF_SYSTEM ||
            activeDrawableGroup?.type === DRAWABLE_TYPES.ROOF_SYSTEMS)

    // formData is user selected value, if it's undefined and not floor/roof system and not specified to use placeholder
    // then set default value
    if (isUndefined(formData) && !is2DDirectionFieldInFloorOrRoofSystem && !hasPlaceholder) {
        onChange(schema?.default || schema?.enum?.[0])
    }

    const handleDropdownChange = (e: ChangeEvent<HTMLSelectElement>) => {
        // by default when we select placeholder schema use undefined, but with case of ui:placeholder
        // we set empty string which brake schema conditions, set is as undefined to keep the same functionality
        // that schema provides
        onChange(e.target.value === '' ? undefined : e.target.value)
    }

    const isMissingRequiredValue = (formData === undefined || formData === null || formData === '') && required

    return (
        <div className="DropdownField">
            <div className="field">
                <div className={`label ${isMissingRequiredValue ? 'text-danger' : ''}`}>
                    {schema.title || uiSchema?.['ui:title'] || ''}
                    {required ? '*' : ''}
                </div>
                <div className={`field-selection ${symbol ? 'with-symbol' : ''}`}>
                    <select
                        className={`custom-select ${isMissingRequiredValue ? 'is-invalid' : ''}`}
                        disabled={readonly}
                        id={idSchema['$id']}
                        onChange={handleDropdownChange}
                        required={required}
                        value={formData}
                    >
                        {uiSchema?.['ui:placeholder'] && <option value="">{uiSchema['ui:placeholder']}</option>}
                        {schema?.enumNames?.map((option, index) => (
                            <option key={option} value={schema?.enum?.[index] as string}>
                                {option}
                            </option>
                        ))}
                    </select>
                    {symbol && <span className="field-symbol">{symbol as string}</span>}
                </div>
            </div>

            <div className="errors">
                {errorSchema &&
                    Object.keys(errorSchema).map((fieldName: string) => {
                        const error = errorSchema[fieldName]

                        return error && Array.isArray(error.__errors) ? renderErrors(error.__errors, fieldName) : <></>
                    })}
            </div>
        </div>
    )
}
